import { CheckCircleIcon, SparklesIcon } from '@heroicons/react/24/outline';
import useText from '@/hooks/useText';

const PARTNER_LOGOS = [
  { src: '/static/images/partners/Alexion-Logo.png', alt: 'Alexion', className: '' },
  {
    src: '/static/images/partners/KyowaKirin_BrandLogo_colorv2-PhotoRoom-1-1024x216.png',
    alt: 'Kyowa Kirin',
    className: '',
  },
  { src: '/static/images/partners/cerevel-logo.png', alt: 'Cerevel', className: '' },
  {
    src: '/static/images/partners/company-servier.png',
    alt: 'Servier',
    className: 'signup-marketing-partner-logo--scale',
  },
];

const RESULT_HIGHLIGHTS = [
  'Faster exploration of gene-disease-drug relationships',
  'Easier integration across research and computational pipelines',
  'Clear visibility into genetic evidence and therapeutic relevance',
];

const SUMMARY_CARDS = [
  { title: '25+ authoritative', body: 'sources integrated' },
  { title: 'Ontology-aligned', body: 'semantic schema' },
  { title: 'Full provenance', body: 'evidence scoring' },
];

export default function SignupMarketingPanel({
  className = '',
  badgeKey = 'signup.apply_trial.badge',
}: {
  className?: string;
  badgeKey?: string;
}) {
  const badge = useText(badgeKey);

  return (
    <section className={`signup-marketing-panel ${className}`.trim()}>
      <div className="signup-marketing-glow signup-marketing-glow--top" aria-hidden />
      <div className="signup-marketing-glow signup-marketing-glow--bottom" aria-hidden />

      <div className="signup-marketing-content">
        <span className="signup-marketing-badge">
          <SparklesIcon className="signup-marketing-badge-icon" aria-hidden />
          {badge}
        </span>

        <h2 className="signup-marketing-title">
          Access structured <span className="signup-marketing-title-highlight">translational knowledge</span>{' '}
          layer
        </h2>

        <p className="signup-marketing-subtitle">
          DISGENET transforms fragmented biomedical evidence into structured, traceable, and actionable
          knowledge for discovery and therapeutic innovation.
        </p>

        <div className="signup-marketing-partners-panel">
          <p className="signup-marketing-partners-label">Used by industry leaders worldwide</p>
          <div className="signup-marketing-partners-logos">
            {PARTNER_LOGOS.map(logo => (
              <div key={logo.src} className={`signup-marketing-partner-logo ${logo.className}`.trim()}>
                <img src={logo.src} alt={logo.alt} />
              </div>
            ))}
          </div>
        </div>

        <div className="signup-marketing-glass">
          <h3 className="signup-marketing-section-title">Why DISGENET?</h3>
          <p className="signup-marketing-body">
            DISGENET integrates information into structured, evidence-ranked genotype-phenotype-drug
            relationships with full provenance, supported by tools for exploration, prioritization, enrichment
            analysis, and programmatic integration.
          </p>
        </div>

        <div className="signup-marketing-glass signup-marketing-result">
          <div className="signup-marketing-result-copy">
            <h3 className="signup-marketing-section-title">The result</h3>
            <ul className="signup-marketing-result-list">
              {RESULT_HIGHLIGHTS.map(item => (
                <li key={item} className="signup-marketing-result-item">
                  <CheckCircleIcon className="signup-marketing-result-icon" aria-hidden />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="signup-marketing-citations">
            <div className="signup-marketing-citations-ring">
              <div className="signup-marketing-citations-inner">
                <span>+9,200</span>
              </div>
            </div>
            <p className="signup-marketing-citations-label">DISGENET citations</p>
          </div>
        </div>

        <p className="signup-marketing-closing">
          One infrastructure. All therapeutic areas. Trusted since 2010
        </p>

        <div className="signup-marketing-summary-cards">
          {SUMMARY_CARDS.map(card => (
            <div key={card.title} className="signup-marketing-summary-card">
              <p className="signup-marketing-summary-title">{card.title}</p>
              <p className="signup-marketing-summary-body">{card.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
